'use client';
import { UploadIcon } from '@/assets/Upload';
import { ChangeEvent, MouseEvent, useRef, useState } from 'react';
import { FileEarmarkImageIcon } from '@/assets/FileEarmarkImageIcon';
import { WindowCloseLineIcon } from '@/assets/WindowCloseLine';
import { ChangeStepFormEvent } from './multi-step-form/multi-step-form.model';

type Props = {
  onFileChange?: (value: File | undefined) => void;
  value?: File;
  maxFileSize?: number; // validate the maximum file size supported
  onChange: (value: ChangeStepFormEvent) => void;
  name: string;
};
const ACCEPTED_FILE_TYPES = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
} as const;
type FileStatus = 'initial' | 'uploading' | 'success';
export const FileUpload = ({
  onFileChange,
  maxFileSize = 10,
  value,
  onChange,
  name,
}: Props) => {
  const [isFileValid, setIsFileValid] = useState(!!value);
  const [isFileOverSize, setIsFileOverSize] = useState(false);
  const [hasWrongExtension, setHasWrongExtension] = useState(false);
  const [file, setFile] = useState<File | undefined>(value);
  const fileName = file ? file.name : '';
  const [uploadStatus, setUploadStatus] = useState<FileStatus>(
    value ? 'success' : 'initial',
  );
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const maxSizeInBytes = 1024 * 1024 * maxFileSize; // MB

  const emitChange = (file?: File) => {
    onChange({ target: { value: file, name } });
    setFile(file);
    onFileChange?.(file);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileHasValidSize = file.size <= maxSizeInBytes;
      const fileHasValidExtension =
        !!ACCEPTED_FILE_TYPES[file.type as keyof typeof ACCEPTED_FILE_TYPES];
      setIsFileOverSize(!fileHasValidSize);
      setHasWrongExtension(!fileHasValidExtension);
      setIsFileValid(fileHasValidSize && fileHasValidExtension);
      const validatedFile =
        fileHasValidSize && fileHasValidExtension ? file : undefined;
      emitChange(validatedFile);
      if (validatedFile) {
        handleUpload();
      }
    }
  };

  const handleOpenFile = (e: MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const getFileSizeMB = (size: number): string => {
    const mb = 1024 * 1024;

    if (size < mb) {
      return `${(size / 1024).toFixed(2)}KB`;
    }
    return `${(size / mb).toFixed(2)}MB`;
  };

  const handleUpload = () => {
    setUploadStatus('uploading');

    // Simulating file upload with progress
    const uploadSimulation = setInterval(() => {
      setUploadProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(uploadSimulation);
          setUploadStatus('success');
          return 100;
        }
        return prevProgress + Math.floor(Math.random() * 60);
      });
    }, 300);

    // In real implementation, you would use FormData and fetch/axios:
  };
  const handleDiscard = () => {
    emitChange();
    setUploadStatus('initial');
    setUploadProgress(0);
    setIsFileOverSize(false);
    setIsFileValid(false);
    setHasWrongExtension(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className="file-upload-container border-1.5 border-dashed flex justify-between py-4 pr-6 pl-6 h-[80px] items-center bg-white rounded-[10px] gap-4 w-full lg:max-w-[52%]"
      style={{
        borderColor:
          !isFileOverSize && !hasWrongExtension
            ? isFileValid
              ? '#069183'
              : 'rgba(0,0,0,0.25)'
            : 'red',
      }}
    >
      {uploadStatus === 'initial' && (
        <>
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            accept=".pdf,.jpg,.jpeg,.png"
            hidden
            onChange={(e) => handleOnChange(e)}
          />
          <div className="flex gap-3 items-center truncate flex-1">
            <UploadIcon />
            <div className="flex flex-col gap-1 flex-1">
              <h6 className="font-semibold text-sm">Select a file</h6>
              <div className="text-xs text-gray-400 truncate">
                File limit {maxFileSize}MB max.{' '}
                {fileName ? `(${fileName})` : ''}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="button-filled"
            onClick={(e) => handleOpenFile(e)}
          >
            Select file
          </button>
        </>
      )}
      {uploadStatus !== 'initial' && (
        <div className="flex gap-3 items-center flex-1 relative justify-between bg-tertiary-gray-light p-4 rounded-[5px] w-full">
          <div className="flex items-center gap-3 flex-1 max-w-[calc(100%_-_50px)] truncate">
            <span className="text-primary-green">
              <FileEarmarkImageIcon />
            </span>
            <div className="flex flex-col gap-2 flex-1 max-w-[calc(100%_-_50px)] truncate">
              <span className="text-xs flex-1 truncate">{file!.name}</span>
              {uploadStatus === 'uploading' && (
                <div className="w-full h-2 rounded-full bg-tertiary-gray overflow-hidden">
                  <div
                    className="h-full bg-primary-green transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
          <span className="text-xs text-gray-400">
            {getFileSizeMB(file!.size as number)}
          </span>
          <button
            className="border-none absolute -right-1 -top-1 text-white p-1 flex items-center justify-center rounded-full h-6 w-6 bg-primary-green hover:saturate-150 transition-all duration-150"
            onClick={handleDiscard}
          >
            <WindowCloseLineIcon />
          </button>
        </div>
      )}
    </div>
  );
};
